import { ref, computed } from 'vue'
import { useQueries } from '@tanstack/vue-query'
import {
	requestArgsBuilder,
	queryKeyHistoryBuilder,
	PASSPORT_DATE_PATHS,
} from './stationPassport.js'
import {
	readResourceItem,
	RESOURCE_NAMES,
	FIELD_NAMES,
	parseResource,
} from '@/api/api.js'
import { useAccontStore } from '@/stores/AccountStore.js'




export function useStationsPassportHistory(stationIds=[], from=null, to=null) {
	// console.log('useStationsPassportHistory call', stationIds, from, to)

	const accountStore = useAccontStore()

	// Reactive period and ids
	const fromTime = ref(from)
	const toTime = ref(to)
	const curStationIds = ref(stationIds)

	const queriesOptions = computed(() => {
		if (curStationIds.value.length === 0) {
			return []
		}

		return curStationIds.value.map((id) => ({
			queryKey: queryKeyHistoryBuilder(
				id,
				fromTime.value,
				toTime.value,
			),
			queryFn: async () => {
				// console.log('fetch StationsPassportHistory', queryKeyHistoryBuilder(id,fromTime.value,toTime.value))
				const history = []
				try {
					// Read with time range
					const response = await readResourceItem(
						RESOURCE_NAMES.stationPassports,
						id,
						accountStore.userLogin,
						accountStore.userPassword,
						requestArgsBuilder(fromTime.value, toTime.value),
					)
					history.push(...response._items.map((item) => parseResource(item, PASSPORT_DATE_PATHS)))
				} catch (e) {
					if (e.cause.status == 500) {
						// Pass internal server error in case when no station history in DB
					} else {
						console.error(`Error on read station ${id} passport`, e)
					}
					
					return history
				}

				// Remove last item if it is not in time interval (need for eve API endpoint)
				const lastItemUpdateTime = history.at(-1)[FIELD_NAMES.updateTime].getTime()
				if (history.length > 0
					&& (lastItemUpdateTime > toTime.value?.getTime()
					|| lastItemUpdateTime < fromTime.value?.getTime())
				) {
					history.splice(-1, 1)
				}

				// Read lowest document version in records
				const lowestVersion = history[0]?.[FIELD_NAMES.version]

				// If lowest version is not first
				if (Number.isInteger(lowestVersion) && lowestVersion > 1) {
					try {
						// Read previous version for correct difference defining
						const prevResp = await readResourceItem(
							RESOURCE_NAMES.stationPassports,
							id,
							accountStore.userLogin,
							accountStore.userPassword,
							requestArgsBuilder(null, null, lowestVersion - 1),
						)

						// If previous version is readed
						if (prevResp) {
							delete prevResp._items // delete artifacts
							// Add to history as first record
							history.unshift(parseResource(prevResp, PASSPORT_DATE_PATHS))
						}
					} catch (e) {
						if (e.cause.status == 500) {
							// Pass internal server error in case when no station history in DB
						} else {
							console.error(`Error on read station ${id} passport`, e)
						}

						return history
					}
				}

				return history
			},
			// Keep results warm to reduce repeated network requests.
			staleTime: 30 * 60 * 1000,
		}))
	})

	// Call useQueries once in setup/context; pass computed queries so it can track changes
	const results = useQueries({ queries: queriesOptions })

	const stationsPassportHistory = computed(() => {
		const history = {}

		for (let i = 0; i < results.value.length; i++) {
			const stationId = curStationIds.value[i]
			history[stationId] = results.value[i]?.data ?? []
		}

		return history
	})

	// Is any query is in loading state
	const isLoading = computed(() => {
		return queriesOptions.value.length === 0
			? false 
			: results.value.some((q) => q.isLoading)
	})

	// Is any query is in error state
	const isError = computed(() => {
		return queriesOptions.value.length === 0
			? false
			: results.value.some((q) => q.isError)
	})

	// Show first error in all queryes
	const error = computed(() => {
		return queriesOptions.value.length === 0
			? null
			: results.value.find((q) => q.isError)?.error ?? null
	})

	// Reset sttation Ids array
	function setStationIds(newStationIds) {
		// console.log('setStationIds', newStationIds)
		if (!Array.isArray(newStationIds)) {
			return
		}

		curStationIds.value = newStationIds
	}

	// Reset period
	function setPeriod(newFromTime, newToTime) {
		// console.log('setPeriod', newFromTime, newToTime)
		if (!(newFromTime instanceof Date)
			|| !(newToTime instanceof Date)
		) {
			console.error('setPeriod error: not Date type input')
			return
		}
		
		fromTime.value = new Date(newFromTime)
		toTime.value = new Date(newToTime)
	}

	return {
		stationsPassportHistory,
		
		isLoading,
		isError,
		error,

		setStationIds,
		setPeriod,
	}
}